/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes, Slot } from '@adobe-commerce/elsie/lib';
import { memo, useMemo } from 'preact/compat';
import {
  Field,
  Input,
  Picker,
  Checkbox,
  InputDate,
  TextArea,
} from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { FieldEnumList } from '@/account/data/models';
import { FieldsProps, FormInputsProps } from '@/account/types';

export const FormInputs: FunctionComponent<FormInputsProps> = memo(
  ({
    loading,
    values,
    fields = [],
    errors,
    className = '',
    onChange,
    onBlur,
    onFocus,
    slots,
    fieldIdPrefix,
  }) => {
    const itemClassName = `${className}__field`;

    // Build translation keys dynamically from all fields
    const translationKeys = useMemo(
      () =>
        Object.fromEntries(
          fields.flatMap((field: FieldsProps) => [
            [
              `${field.code}.label`,
              `Account.AddressForm.fields.${field.code}.label`,
            ],
            [
              `${field.code}.placeholder`,
              `Account.AddressForm.fields.${field.code}.placeholder`,
            ],
          ])
        ),
      [fields]
    );

    const translations = useText(translationKeys);

    const getAriaLabel = (item: FieldsProps) =>
      translations[`${item.code}.label`] || item.label;

    const getFloatingLabel = (item: FieldsProps) =>
      `${getAriaLabel(item)} ${item.required ? '*' : ''}`;

    const getPlaceholder = (item: FieldsProps) =>
      translations[`${item.code}.placeholder`] || item.label;

    const renderSlot = (item: any, errorMessage: string) => {
      if (!slots?.[`AddressFormInput_${item.code}`]) return;

      const contextValue = {
        inputName: item.customUpperCode,
        handleOnChange: onChange,
        handleOnBlur: onBlur,
        handleOnFocus: onFocus,
        errorMessage,
        errors,
        config: item,
      };

      return (
        <Slot
          data-testid={`addressFormInput_${item.code}`}
          name={`AddressFormInput_${item.code}`}
          slot={slots[`AddressFormInput_${item.code}`]}
          context={contextValue}
          key={item.id}
        />
      );
    };

    const selectElement = (
      item: any,
      valueMessage: string,
      errorMessage: string
    ) => {
      const defaultSelectValue =
        item.options.find(
          (option: { isDefault: boolean; value: string; text: string }) =>
            option.isDefault
        )?.value ??
        valueMessage ??
        item.defaultValue;

      return (
        <>
          {renderSlot(item, errorMessage) ?? (
            <Field
              key={item.id}
              error={errorMessage}
              className={classes([
                itemClassName,
                `${itemClassName}--${item.id}`,
                [`${itemClassName}--${item.id}-hidden`, item.isHidden],
                item.className,
              ])}
              data-testid={`${className}--${item.id}`}
              disabled={loading || item.disabled}
            >
              <Picker
                aria-label={getAriaLabel(item)}
                aria-required={item.required ? 'true' : 'false'}
                id={fieldIdPrefix ? `${fieldIdPrefix}_${item.code}` : item.code}
                required={item?.required}
                name={item.customUpperCode}
                floatingLabel={getFloatingLabel(item)}
                placeholder={getPlaceholder(item)}
                options={item.options}
                onBlur={onBlur}
                onFocus={onFocus}
                handleSelect={onChange}
                defaultValue={defaultSelectValue}
                value={defaultSelectValue}
              />
            </Field>
          )}
        </>
      );
    };

    const inputElement = (
      item: any,
      valueMessage: string,
      errorMessage: string
    ) => {
      return (
        <>
          {renderSlot(item, errorMessage) ?? (
            <Field
              key={item.id}
              error={errorMessage}
              className={classes([
                itemClassName,
                `${itemClassName}--${item.id}`,
                [`${itemClassName}--${item.id}-hidden`, item.isHidden],
                item.className,
              ])}
              data-testid={`${className}--${item.id}`}
              disabled={loading}
            >
              <Input
                aria-label={getAriaLabel(item)}
                aria-required={item.required ? 'true' : 'false'}
                id={fieldIdPrefix ? `${fieldIdPrefix}_${item.code}` : item.code}
                type="text"
                name={item.customUpperCode}
                value={valueMessage ?? item.defaultValue}
                placeholder={getPlaceholder(item)}
                floatingLabel={getFloatingLabel(item)}
                onBlur={onBlur}
                onFocus={onFocus}
                onChange={onChange}
              />
            </Field>
          )}
        </>
      );
    };

    const inputDateElement = (
      item: any,
      valueMessage: string,
      errorMessage: string
    ) => {
      return (
        <>
          {renderSlot(item, errorMessage) ?? (
            <Field
              key={item.id}
              error={errorMessage}
              className={classes([
                itemClassName,
                `${itemClassName}--${item.id}`,
                [`${itemClassName}--${item.id}-hidden`, item.isHidden],
                item.className,
              ])}
              data-testid={`${className}--${item.id}`}
              disabled={loading || item.disabled}
            >
              <InputDate
                aria-label={getAriaLabel(item)}
                aria-required={item.required ? 'true' : 'false'}
                id={fieldIdPrefix ? `${fieldIdPrefix}_${item.code}` : item.code}
                type="text"
                name={item.customUpperCode}
                value={valueMessage || item.defaultValue}
                placeholder={getPlaceholder(item)}
                floatingLabel={getFloatingLabel(item)}
                onBlur={onBlur}
                onChange={onChange}
                disabled={loading || item.disabled}
              />
            </Field>
          )}
        </>
      );
    };

    const inputCheckBoxElement = (
      item: any,
      valueMessage: string,
      errorMessage: string
    ) => {
      return (
        <>
          {renderSlot(item, errorMessage) ?? (
            <Field
              key={item.id}
              error={errorMessage}
              className={classes([
                itemClassName,
                `${itemClassName}--${item.id}`,
                [`${itemClassName}--${item.id}-hidden`, item.isHidden],
                item.className,
              ])}
              data-testid={`${className}--${item.id}`}
              disabled={item?.isHidden ?? loading}
            >
              <Checkbox
                aria-label={getAriaLabel(item)}
                aria-required={item.required ? 'true' : 'false'}
                aria-hidden={item.isHidden}
                tabindex={item?.isHidden ? -1 : 0}
                id={fieldIdPrefix ? `${fieldIdPrefix}_${item.code}` : item.code}
                name={item.customUpperCode}
                checked={valueMessage || item.defaultValue}
                placeholder={getPlaceholder(item)}
                label={getFloatingLabel(item)}
                onBlur={onBlur}
                onChange={onChange}
              />
            </Field>
          )}
        </>
      );
    };

    const textAreaElement = (
      item: any,
      valueMessage: string,
      errorMessage: string
    ) => {
      return (
        <>
          {renderSlot(item, errorMessage) ?? (
            <Field
              key={item.id}
              error={errorMessage}
              className={classes([
                itemClassName,
                `${itemClassName}--${item.id}`,
                [`${itemClassName}--${item.id}-hidden`, item.isHidden],
                item.className,
              ])}
              data-testid={`${className}--${item.id}`}
              disabled={loading}
            >
              <TextArea
                aria-label={getAriaLabel(item)}
                aria-required={item.required ? 'true' : 'false'}
                id={fieldIdPrefix ? `${fieldIdPrefix}_${item.code}` : item.code}
                type="text"
                name={item.customUpperCode}
                value={valueMessage ?? item.defaultValue}
                label={getFloatingLabel(item)}
                onBlur={onBlur}
                onChange={onChange}
              />
            </Field>
          )}
        </>
      );
    };

    if (!fields.length) return null;

    return (
      <>
        {fields.map((item: FieldsProps) => {
          const errorMessage = errors?.[item.customUpperCode] as string;
          const valueMessage = values?.[item.customUpperCode] as string;

          switch (item.fieldType) {
            case FieldEnumList.TEXT: {
              if (item.options.length) {
                return selectElement(item, valueMessage, errorMessage);
              }

              return inputElement(item, valueMessage, errorMessage);
            }

            case FieldEnumList.MULTILINE: {
              return inputElement(item, valueMessage, errorMessage);
            }

            case FieldEnumList.SELECT: {
              return selectElement(item, valueMessage, errorMessage);
            }

            case FieldEnumList.DATE: {
              return inputDateElement(item, valueMessage, errorMessage);
            }

            case FieldEnumList.BOOLEAN: {
              return inputCheckBoxElement(item, valueMessage, errorMessage);
            }

            case FieldEnumList.TEXTAREA: {
              return textAreaElement(item, valueMessage, errorMessage);
            }

            default:
              return null;
          }
        })}
      </>
    );
  }
);
